import React, { useEffect, useState, useMemo, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { fetchWarehousesAndAppliances, changePage, clear } from '#action/warehouse/warehouses';
import { updateBreadcrumb } from '#action/breadcrumb';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { isFilterExist, getRelatedTo } from '#component/general/list-filter/helper';
import WarehouseActions from './warehouse-actions';
import { combineWarehousesAppliances } from './helper';
import { Warehouses } from './warehouses';
import {
  Root,
  Container,
  LoadingContainer,
  FilterButtonContainerSiteManagerAddSite,
  AddWarehouseButton,
  AddButtonLabel,
  StyledPagination,
  PaginationPlaceholder,
  MarginTopThree,
  TextCenter
} from './styles';
import filterConfig from './warehouse-filter/config';
import SitesMobileView from './mobile-view';
import { setDocumentTitle, updateURLParameter } from '#helper';
import { useUrlPagination } from '#hooks';

const SiteManager = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    warehouses: paginatedWarehouses,
    page,
    pages,
    status
  } = useSelector(s => s.warehousesPaginated, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const { userType } = account;
  const { appliances, status: applianceStatus } = useSelector(s => s.appliances, shallowEqual);
  const relatedTo = getRelatedTo();
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const siteManagerFilter = listFilter[relatedTo];
  const filterLoaded = listFilter.status === 'succeeded';
  const filterData = useMemo(() => filterConfig, []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [warehouse, setWarehouse] = useState({});
  const [showAllIndex, setShowAllIndex] = useState(+localStorage.getItem('showAllAppliances'));
  const [init, setInit] = useState(false);
  const loading = ['loading', 'idle'].includes(status) || !init;
  const prevWarehousesRef = useRef();
  const dataRequestingRef = useRef();

  useUrlPagination({ page, pages, changePage });

  useEffect(() => {
    setDocumentTitle('Sites');
    dispatch(updateBreadcrumb([
      { label: 'Sites', path: '/warehouse' },
      { label: 'Site management' }
    ]));
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        dispatch(fetchWarehousesAndAppliances())
      ]);

      setInit(true);
    };

    if (filterLoaded && !dataRequestingRef.current) {
      dataRequestingRef.current = true;
      fetchData();
    }

    return () => {
      clear();
    };
  }, [dispatch, filterLoaded]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const paginatedWarehousesWithAppliances = useMemo(() => {
    if (!init) return [];

    if (loading) {
      return prevWarehousesRef.current;
    }

    const data = combineWarehousesAppliances(paginatedWarehouses, appliances);
    prevWarehousesRef.current = data;

    return data;
  }, [appliances, init, loading, paginatedWarehouses]);

  const handlePageChange = async (event, newPage) => {
    if (page !== newPage) {
      updateURLParameter('page', newPage);
      dispatch(changePage(newPage));
    }
  };

  const emptySearch = siteManagerFilter?.search?.value === '';
  const filterExist = isFilterExist(siteManagerFilter) || emptySearch;
  const emptyFilter = listFilter?.pristine ? filterExist : true;
  const isNoData = !loading && !paginatedWarehousesWithAppliances.length &&
    (!filterExist && listFilter?.pristine);

  if (isMobile) {
    return <SitesMobileView
      applianceStatus={applianceStatus}
      isFilterSelected={!emptyFilter}
      page={page}
      userType={userType}
      paginatedWarehousesWithAppliances={paginatedWarehousesWithAppliances}
    />;
  }

  const ContentWrapper = !paginatedWarehousesWithAppliances?.length && userType < 10 ?
    ({ children }) => <MarginTopThree><TextCenter>{children}</TextCenter></MarginTopThree> :
    React.Fragment;

  const ContainerWrapper = loading ? LoadingContainer : Container;

  return (
    <Root>
      <FilterButtonContainerSiteManagerAddSite>
        <FilterWrapper data={filterData} loading={loading} />
        {userType < 10 &&
          <AddWarehouseButton
            size="small"
            color="secondary"
            aria-label="Add warehouse"
            component={Link}
            to="/create-warehouse"
          >
            <AddIcon />
            <AddButtonLabel>
              Add site
            </AddButtonLabel>
          </AddWarehouseButton>}
      </FilterButtonContainerSiteManagerAddSite>
      <ContainerWrapper>
        <ContentWrapper>
          <Warehouses
            warehouses={paginatedWarehousesWithAppliances}
            loading={loading}
            filterExist={filterExist}
            applianceStatus={applianceStatus}
            userType={userType}
            showAllIndex={showAllIndex}
            setShowAllIndex={setShowAllIndex}
            setWarehouse={setWarehouse}
            setAnchorEl={setAnchorEl}
            noData={isNoData}
          />
        </ContentWrapper>
      </ContainerWrapper>
      {(!!pages && Number(pages) > 1) ? (
        <StyledPagination
          shape="rounded"
          color="secondary"
          count={pages}
          page={page}
          onChange={handlePageChange}
        />
      ) : (
        <PaginationPlaceholder />
      )}
      <WarehouseActions
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        warehouse={warehouse}
      />
    </Root>
  );
};

export default SiteManager;
